import { api } from '@packages/backend/convex/_generated/api';
import type { DocMigrationCvx } from '@packages/shared';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { toast } from 'sonner';
import { CardTitle } from '@/components/ui/card';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';

export const Route = createFileRoute('/a/doc-migrations')({
  component: Component,
});

function Component() {
  const convex = useConvex();
  const { data, isError, isPending } = useQuery(api.docMigrations.list);
  const [isApplying, setIsApplying] = useState(false);

  if (isPending) return <LoadingIcon />;
  if (isError) return null; // Handle error in the parent component
  const docMigrations: DocMigrationCvx[] = data;

  const applyPendingDocs = async () => {
    setIsApplying(true);
    try {
      await convex.mutation(api.docMigrations.applyPendingDocs);
      toast.success('Started migration of docs...');
    } catch (error) {
      toast.error("Couldn't apply changes", {
        description: getUserErrorMessage(error),
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div>
      <CardTitle>Doc Migrations</CardTitle>
      {docMigrations.length > 0 ? (
        <ActionButton
          className={'my-4'}
          onClick={applyPendingDocs}
          variant="destructive"
          loading={isApplying}
          disabled={isApplying}
        >
          Start migrating docs
        </ActionButton>
      ) : (
        <div className="text-muted-foreground text-sm mt-2">All done.</div>
      )}
      {docMigrations.map(({ doc, docType, pendingDoc }, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: No useful key to use here
        <div className="overflow-x-auto text-xs bg-muted border rounded-sm" key={index}>
          <ReactDiffViewer
            compareMethod={DiffMethod.WORDS}
            hideLineNumbers={true}
            leftTitle={`${docType} ${JSON.parse(doc)._id}`}
            newValue={pendingDoc}
            oldValue={doc}
            renderContent={(str) => (
              <pre
                dangerouslySetInnerHTML={{
                  __html: str,
                }}
                style={{ lineHeight: 1.0 }}
              />
            )}
            rightTitle={'Pending Document'}
            showDiffOnly={true} // It's all one line number as far as differ is concerned, so it doesn't matter.
            splitView={true}
            styles={{
              contentText: {
                fontSize: '11px',
              },
              diffAdded: {
                padding: '0px',
              },
              // https://www.npmjs.com/package/react-diff-viewer#overriding-styles
              variables: {
                // When useDarkTheme={true} set below
                dark: {},
                light: {
                  codeFoldBackground: '#f7f7f7',
                  codeFoldContentColor: '#666',
                  diffViewerBackground: '#fff',
                  diffViewerColor: 'black',
                  diffViewerTitleBackground: '#eee',
                  diffViewerTitleBorderColor: '#ccc',
                },
              },
            }}
            useDarkTheme={false}
          />
        </div>
      ))}
    </div>
  );
}
